// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  type AutodeskCloudModel,
  type WorkOrderId,
  type HexTriplet,
  useEvolveGet,
  useEvolvePaginatedGet,
  type MappedSpool,
} from '@evolvemep/api-ts';

import type { RenderableSpool } from 'components/Autodesk/extensions/SpoolsExtension/types';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { GOLDEN_ANGLE } from 'helpers/createColorScheme';
import { getAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { isNotNil } from 'helpers/isNotNil';
import { strongEntries, strongValues } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { type ColorSchemeOptions, useSpoolColorScheme } from 'modules/Shop/Spooling/hooks/useSpoolColorScheme';
import { useSpoolSelectionAndVisibility } from 'modules/Shop/Spooling/hooks/useSpoolSelectionAndVisibility';

export type MappedRenderableSpool = RenderableSpool & MappedSpool;

type Args = {
  model: AutodeskCloudModel | null;
};

const unassignedColorSchemeOptions: ColorSchemeOptions<RenderableSpool, RenderableSpool['name']> = {
  getIdentifier: (spool: RenderableSpool) => spool.name,
};

const assignedColorSchemeOptions: ColorSchemeOptions<MappedSpool, MappedSpool['workOrderId']> = {
  colorSchemeArgs: [GOLDEN_ANGLE / 3],
  getIdentifier: (ms: MappedSpool) => ms.workOrderId,
};

type SpoolAssignmentContextType = {
  unassignedSpools: readonly RenderableSpool[];
  assignedSpools: readonly [WorkOrderId, readonly MappedRenderableSpool[]][];
  assignedSpoolData: readonly MappedSpool[];
  loadingAssignedSpools: boolean;
  loadingSpoolsReadyForFabrication: boolean;
  refreshSpools: (loadedModel: AutodeskCloudModel) => Promise<void>;
  unassignedSpoolColorScheme: Record<string, HexTriplet>;
  assignedSpoolColorScheme: Record<WorkOrderId, HexTriplet>;
  updateUnassignedSpoolColor: (id: string, color: HexTriplet) => void;
  updateAssignedSpoolColor: (id: WorkOrderId, color: HexTriplet) => void;
  /** Set of spool names that have associated fabrication elements. */
  spoolNamesWithFabricationElements: ReadonlySet<string>;
} & ReturnType<typeof useSpoolSelectionAndVisibility>;

const SpoolAssignmentContext = createContext<SpoolAssignmentContextType | undefined>(undefined);

type SpoolAssignmentContextProviderProps = Args & { children: ReactNode };

/**
 * Loads the mapped/assigned spools for a given model, then partitions the spools into unmapped and mapped.
 * Also provides color schemes for each partition, and visibility/selection state for the spools.
 */
export const SpoolAssignmentProvider = ({ children, model }: SpoolAssignmentContextProviderProps) => {
  const spoolLookup = useAutodeskExtensionStoreSnapshot('spools');
  const spools = useMemo(() => (isNotNil(spoolLookup) ? strongValues(spoolLookup) : null), [spoolLookup]);

  const { fetchPage: fetchPageAssignedSpools, loading: loadingAssignedSpools } = useEvolvePaginatedGet('shop/spool');
  const [assignedSpoolData, setAssignedSpoolData] = useState<MappedSpool[]>([]);

  const { apiCall: fetchSpoolsReadyForFabrication, loading: loadingSpoolsReadyForFabrication } = useEvolveGet(
    'shop/fabrication/element/spoolsReadyForFabrication',
  );
  const [spoolNamesWithFabricationElements, setSpoolNamesWithFabricationElements] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const hasFabricationAccess = useHasAccess('fabrication', 'read');

  // Create a data loader and run it when the model is done loading
  const refreshSpools = useCallback(
    async (loadedModel: AutodeskCloudModel) => {
      setAssignedSpoolData([]);
      setSpoolNamesWithFabricationElements(new Set());
      const spoolDataPromises = [
        getAllDataFromFetcher(fetchPageAssignedSpools, {
          params: {
            projectId: loadedModel.projectId,
            urn: encodeURIComponent(loadedModel.urn),
          },
        }).then((r) => setAssignedSpoolData(r.filter((s) => isNotNil(s.workOrderId)))),
      ];

      if (hasFabricationAccess) {
        spoolDataPromises.push(
          fetchSpoolsReadyForFabrication({ params: { projectId: loadedModel.projectId } }).then((r) =>
            setSpoolNamesWithFabricationElements(new Set(r.map((s) => s.name))),
          ),
        );
      }
      await Promise.allSettled(spoolDataPromises);
    },
    [fetchPageAssignedSpools, fetchSpoolsReadyForFabrication, hasFabricationAccess],
  );
  useEffect(() => {
    if (isNotNil(model)) {
      void refreshSpools(model);
    }
  }, [refreshSpools, model]);

  // Partition spools into unmapped and mapped.
  const [unassignedSpools, assignedSpools] = useMemo<
    [RenderableSpool[], [WorkOrderId, MappedRenderableSpool[]][]]
  >(() => {
    const { unassigned, assigned } = (spools ?? []).reduce(
      (out, curr) => {
        const workOrder = assignedSpoolData.find((s) => s.spool === curr.name);
        if (workOrder) {
          out.assigned[workOrder.workOrderId] = [
            ...(out.assigned[workOrder.workOrderId] ?? []),
            {
              ...curr,
              ...workOrder,
            },
          ];
        } else {
          out.unassigned.push(curr);
        }
        return out;
      },
      {
        unassigned: [] as RenderableSpool[],
        assigned: {} as Record<WorkOrderId, MappedRenderableSpool[]>,
      },
    );
    return [
      unassigned,
      strongEntries(assigned).toSorted(([_, [a]], [__, [b]]) => {
        const { name: aName } = a ?? {};
        const { name: bName } = b ?? {};
        if (aName && bName) return aName.localeCompare(bName);
        return 0;
      }),
    ] as const;
  }, [spools, assignedSpoolData]);

  const {
    colorScheme: unassignedSpoolColorScheme,
    initializeColorScheme: initializeSpoolColorScheme,
    updateColor: updateUnassignedSpoolColor,
  } = useSpoolColorScheme(unassignedColorSchemeOptions);
  const {
    colorScheme: assignedSpoolColorScheme,
    initializeColorScheme: initializeMappedColorScheme,
    updateColor: updateAssignedSpoolColor,
  } = useSpoolColorScheme(assignedColorSchemeOptions);
  useEffect(() => {
    initializeSpoolColorScheme(spools);
  }, [initializeSpoolColorScheme, spools]);
  useEffect(() => {
    initializeMappedColorScheme(assignedSpoolData);
  }, [assignedSpoolData, initializeMappedColorScheme]);

  const selectionAndVisibilityProps = useSpoolSelectionAndVisibility(model);

  const { refreshSpoolVisibility } = selectionAndVisibilityProps;
  // Any time the unfiltered partition of mapped and unmapped spools changes, refresh the visibility state.
  useEffect(() => {
    refreshSpoolVisibility(
      unassignedSpools.map((s) => s.name),
      assignedSpools.flatMap(([, innerSpools]) => innerSpools.map((s) => s.name)),
    );
  }, [refreshSpoolVisibility, unassignedSpools, assignedSpools]);

  const value = {
    unassignedSpools,
    assignedSpools,
    assignedSpoolData,
    loadingAssignedSpools,
    loadingSpoolsReadyForFabrication,
    refreshSpools,
    unassignedSpoolColorScheme,
    assignedSpoolColorScheme,
    updateUnassignedSpoolColor,
    updateAssignedSpoolColor,
    spoolNamesWithFabricationElements,
    ...selectionAndVisibilityProps,
  } as const;

  return <SpoolAssignmentContext value={value}>{children}</SpoolAssignmentContext>;
};

export const useSpoolAssignment = () => useGeneralContext(SpoolAssignmentContext, 'SpoolAssignmentContext');
